<template>
  <div class="page-shell layout-shell">
    <aside class="side-nav">
      <div class="brand-block">
        <strong>配送系统</strong>
      </div>
      <el-menu
        class="side-menu"
        :default-active="$route.path"
        background-color="transparent"
        text-color="#35507d"
        active-text-color="#6ea8ff"
        @select="handleSelect"
      >
        <el-menu-item v-if="canVisitDashboard" index="/dashboard">数据看板</el-menu-item>
        <el-menu-item v-if="canVisitOrders" index="/orders">订单管理</el-menu-item>
        <el-menu-item v-if="canVisitApiOrderAllocation" index="/api-order-allocation">API订单分配</el-menu-item>
        <el-menu-item v-if="canVisitUsers" index="/users">用户管理</el-menu-item>
        <el-menu-item v-if="canVisitCouriers" index="/couriers">配送员管理</el-menu-item>
        <el-menu-item v-if="canVisitBusinessTypes" index="/business-types">业务类型</el-menu-item>
        <el-menu-item v-if="canVisitProducts" index="/products">产品列表</el-menu-item>
        <el-menu-item v-if="canVisitPaymentTypes" index="/payment-types">支付方式</el-menu-item>
        <el-menu-item v-if="canVisitOrderSources" index="/order-sources">订单来源</el-menu-item>
        <el-menu-item v-if="canVisitUpstreamApps" index="/upstream-apps">应用管理</el-menu-item>
        <el-menu-item v-if="canVisitUpstreamApiLogs" index="/upstream-api-logs">接口调用日志</el-menu-item>
        <el-menu-item v-if="canVisitSmsTemplates" index="/sms-templates">短信模板</el-menu-item>
        <el-menu-item v-if="canVisitSmsLogs" index="/sms-send-logs">短信日志</el-menu-item>
        <el-menu-item v-if="canVisitAttachmentRetention" index="/attachment-retention">附件管理</el-menu-item>
        <el-menu-item v-if="canVisitManualOrderSettings" index="/manual-order-settings">录单字段配置</el-menu-item>
        <el-menu-item v-if="canVisitSystemSettings" index="/system-settings">系统设置</el-menu-item>
        <el-menu-item v-if="canVisitAdminRelations" index="/admin-relations">关联分管</el-menu-item>
        <el-menu-item v-if="canVisitSecurityMenus" index="/ip-blacklist">IP黑名单</el-menu-item>
        <el-menu-item v-if="canVisitSecurityMenus" index="/login-logs">登录日志</el-menu-item>
        <el-menu-item v-if="canVisitSecurityMenus" index="/login-alerts">登录告警</el-menu-item>
        <el-menu-item v-if="canVisitOperationLogs" index="/operation-logs">操作日志</el-menu-item>
        <el-menu-item v-if="canVisitProfile" index="/profile">个人中心</el-menu-item>
      </el-menu>
    </aside>
    <main class="main-panel">
      <div class="topbar">
        <div class="topbar-left">
          <div class="page-title">{{ roleText }} / {{ currentUser.name || currentUser.username || "-" }}</div>
        </div>
        <div class="topbar-right">
          <span class="user-meta">{{ roleText }}</span>
          <span class="user-meta">{{ currentUser.name || "-" }}</span>
          <el-button size="mini" plain @click="logout">退出</el-button>
        </div>
      </div>
      <router-view />
      <IcpFooter />
    </main>
  </div>
</template>

<script>
import IcpFooter from "../components/IcpFooter.vue";
import { fetchOrderStatusCounts } from "../api/orders";
import { getStoredUser, hasRole, roleLabel } from "../utils/auth";

const SALES_ORDER_ALERT_KEY = "ps_sales_order_alert_shown";

export default {
  components: {
    IcpFooter,
  },
  computed: {
    currentUser() {
      return getStoredUser();
    },
    roleText() {
      return roleLabel(this.currentUser.role);
    },
    canVisitDashboard() {
      return hasRole(this.currentUser, ["super_admin", "admin", "sales"]);
    },
    canVisitOrders() {
      return hasRole(this.currentUser, ["super_admin", "admin", "sales"]);
    },
    canVisitApiOrderAllocation() {
      return hasRole(this.currentUser, ["super_admin", "admin", "sales"]);
    },
    canVisitUsers() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canVisitCouriers() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canVisitAdminRelations() {
      return hasRole(this.currentUser, ["super_admin"]);
    },
    canVisitSecurityMenus() {
      return hasRole(this.currentUser, ["super_admin"]);
    },
    canVisitOperationLogs() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canVisitBusinessTypes() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canVisitProducts() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canVisitPaymentTypes() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canVisitOrderSources() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canVisitUpstreamApps() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canVisitUpstreamApiLogs() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canVisitSmsTemplates() {
      return this.currentUser.role === "super_admin" && this.currentUser.username === "admin";
    },
    canVisitSmsLogs() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canVisitAttachmentRetention() {
      return hasRole(this.currentUser, ["super_admin"]);
    },
    canVisitManualOrderSettings() {
      return hasRole(this.currentUser, ["super_admin"]);
    },
    canVisitSystemSettings() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canVisitProfile() {
      return hasRole(this.currentUser, ["super_admin", "admin", "sales"]);
    },
  },
  mounted() {
    this.showSalesOrderAlert();
  },
  methods: {
    async showSalesOrderAlert() {
      if (this.currentUser.role !== "sales") return;
      const alertKey = `${SALES_ORDER_ALERT_KEY}_${this.currentUser.id || this.currentUser.username || "sales"}`;
      if (sessionStorage.getItem(alertKey)) return;
      sessionStorage.setItem(alertKey, "1");
      try {
        const { data } = await fetchOrderStatusCounts();
        const items = data.items || [];
        const pendingReviewCount = this.getStatusCount(items, "待审核");
        const failedCount = this.getStatusCount(items, "失败");
        if (!pendingReviewCount && !failedCount) return;
        this.$alert(
          `待审核订单：${pendingReviewCount}，失败订单：${failedCount}，请到订单管理中处理`,
          "订单处理提醒",
          {
            confirmButtonText: "知道了",
            type: "warning",
          }
        );
      } catch (error) {
        sessionStorage.removeItem(alertKey);
      }
    },
    getStatusCount(items, status) {
      const item = items.find((entry) => entry.status === status);
      return Number(item?.count || 0);
    },
    handleSelect(path) {
      if (this.$route.path === path) return;
      this.$router.push(path).catch((error) => {
        if (error && error.name !== "NavigationDuplicated") {
          throw error;
        }
      });
    },
    logout() {
      localStorage.removeItem("ps_token");
      localStorage.removeItem("ps_user");
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith(SALES_ORDER_ALERT_KEY))
        .forEach((key) => sessionStorage.removeItem(key));
      this.$router.replace("/login");
    },
  },
};
</script>

<style scoped>
.layout-shell {
  display: grid;
  grid-template-columns: 240px 1fr;
  height: 100vh;
  min-height: 0;
  width: 100%;
  overflow: hidden;
}

.side-nav {
  min-height: 0;
  padding: 24px 18px;
  background: #f5f8ff;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.side-nav::-webkit-scrollbar {
  display: none;
}

.brand-block {
  position: sticky;
  top: 0;
  z-index: 5;
  margin-bottom: 24px;
  padding: 16px 18px;
  border-radius: 18px;
  background: #ffffff;
  color: #173c89;
  box-shadow: 0 10px 30px rgba(23, 60, 137, 0.08);
}

.brand-block strong {
  display: block;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 1px;
}

.main-panel {
  min-width: 0;
  min-height: 0;
  padding: 20px 28px 72px;
  background: #f5f8ff;
  overflow-x: hidden;
  overflow-y: auto;
}

.main-panel :deep(.icp-footer) {
  margin-top: 0;
}

.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  min-width: 0;
  margin-bottom: 18px;
  padding: 14px 18px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(23, 60, 137, 0.08);
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #173c89;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.user-meta {
  color: #60718f;
  font-size: 13px;
}

.side-nav :deep(.side-menu) {
  border-right: none;
  background: transparent;
}

.side-nav :deep(.el-menu-item) {
  height: 44px;
  line-height: 44px;
  margin-bottom: 8px;
  padding: 0 14px;
  border-radius: 12px;
  color: #35507d;
  font-size: 15px;
  font-weight: 600;
}

.side-nav :deep(.el-menu-item:hover) {
  background: #edf2f9;
  color: #4f8ff7;
}

.side-nav :deep(.el-menu-item.is-active) {
  background: #eef3f8;
  color: #6ea8ff;
  font-weight: 700;
}
</style>
